/**
 * The confirmation gate.
 *
 * Two ceremonial checkboxes, both required. Modelled on the disable
 * organisation confirmation, but inline rather than in a modal because the
 * operator needs the table visible while they confirm.
 *
 * Totals are always per currency. A batch can span charities and therefore
 * currencies, and a single summed figure here would be the wrong number on the
 * one panel where the operator is attesting that it is right.
 */

import React from 'react';
import { Button, Checkbox, Icon, Message, Segment } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { i18nKeys } from '../constants';
import { formatAmountWithCurrency } from '../helpers';
import { CurrencyTotal } from '../types';
import styles from '../BulkChargeGuarantee.module.css';

interface ChargeConfirmPanelProps {
  chargeableCount: number;
  blockedCount: number;
  totalsByCurrency: CurrencyTotal[];
  confirmListCorrect: boolean;
  confirmIrreversible: boolean;
  onToggleListCorrect: (value: boolean) => void;
  onToggleIrreversible: (value: boolean) => void;
  onCharge: () => void;
}

const ChargeConfirmPanel: React.FC<ChargeConfirmPanelProps> = ({
  chargeableCount,
  blockedCount,
  totalsByCurrency,
  confirmListCorrect,
  confirmIrreversible,
  onToggleListCorrect,
  onToggleIrreversible,
  onCharge
}) => {
  if (chargeableCount === 0) {
    return (
      <Message warning>
        <p>
          <Translate value={i18nKeys.CONFIRM.NOTHING_CHARGEABLE} />
        </p>
        {blockedCount > 0 && (
          <p>{I18n.t(i18nKeys.CONFIRM.BLOCKED_NOTE, { count: blockedCount })}</p>
        )}
      </Message>
    );
  }

  const canCharge = confirmListCorrect && confirmIrreversible;

  return (
    <Segment className={styles.confirmSegment}>
      <h4>
        <Translate value={i18nKeys.CONFIRM.TITLE} />
      </h4>

      <div className={styles.confirmSummary}>
        {totalsByCurrency.map((total) => (
          <div key={total.currency ?? 'default'}>
            {I18n.t(i18nKeys.CONFIRM.SUMMARY, {
              count: total.count,
              total: formatAmountWithCurrency(total.amount, total.currency)
            })}
          </div>
        ))}
      </div>

      {totalsByCurrency.length > 1 && (
        <Message info size="small">
          <Translate value={i18nKeys.CONFIRM.MULTI_CURRENCY_NOTE} />
        </Message>
      )}

      {blockedCount > 0 && (
        <p>{I18n.t(i18nKeys.CONFIRM.BLOCKED_NOTE, { count: blockedCount })}</p>
      )}

      <div className={styles.confirmCheckboxes}>
        <Checkbox
          label={I18n.t(i18nKeys.CONFIRM.CHECKBOX_LIST_CORRECT)}
          checked={confirmListCorrect}
          onChange={(_, data) => onToggleListCorrect(!!data.checked)}
        />
        <Checkbox
          label={I18n.t(i18nKeys.CONFIRM.CHECKBOX_IRREVERSIBLE)}
          checked={confirmIrreversible}
          onChange={(_, data) => onToggleIrreversible(!!data.checked)}
        />
      </div>

      <Button negative disabled={!canCharge} onClick={onCharge}>
        <Icon name="credit card" />
        {I18n.t(i18nKeys.CONFIRM.CHARGE_BUTTON, { count: chargeableCount })}
      </Button>
    </Segment>
  );
};

export default ChargeConfirmPanel;
